import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, CheckCircle2, Clock, Gift, ChevronRight } from 'lucide-react';
import { UpsellPurchaseToast } from '../components/UpsellPurchaseToast';
import { Footer } from '../components/Footer';
export function UpsellPage() {
  useEffect(() => {
    // Load Hotmart Sales Funnel Widget Script
    const script = document.createElement('script');
    script.src = 'https://checkout.hotmart.com/lib/hotmart-checkout-elements.js';
    script.async = true;
    script.onload = () => {
      if (window && (window as any).checkoutElements) {
        ;
        (window as any).checkoutElements.init('salesFunnel').mount('#hotmart-sales-funnel');
      }
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="min-h-screen bg-[#FFF8F0] font-sans selection:bg-[#FFCC80] selection:text-gray-900 pt-10 md:pt-16" data-id="element-938">
      <main className="max-w-3xl mx-auto px-4 pb-20" data-id="element-939">
        {/* SECCIÓN 1: HEADLINE PRINCIPAL */}
        <motion.section {...fadeIn} className="text-center py-10 md:py-16" data-id="element-940">
          <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-6 py-3 rounded-full font-black text-base md:text-lg mb-6 border-2 border-[#4CAF50] shadow-lg shadow-green-200 animate-pulse" data-id="element-941">
            <AlertCircle className="w-6 h-6" data-id="element-942" />
            <span data-id="element-943">ESPERÁ — Tu pedido está casi listo.</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight mb-6" data-id="element-944">
            Pero antes de confirmar, tenés{' '}
            <span className="text-[#E65100]" data-id="element-945">60 segundos</span> para tomar la
            decisión más importante para la salud de tu peque.
          </h1>
        </motion.section>

        {/* SECCIÓN 2: EL GANCHO EMOCIONAL */}
        <motion.section {...fadeIn} className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 mb-12" data-id="element-946">
          <div className="text-lg md:text-xl text-gray-700 space-y-4 italic text-center mb-10" data-id="element-947">
            <p data-id="element-948">Acabás de dar el primer paso.</p>
            <p data-id="element-949">Las recetas de NutriPeques van a cambiar la mesa de tu casa.</p>
            <p className="font-bold text-gray-900" data-id="element-950">
              Pero hay algo que ninguna receta sola puede resolver.
            </p>
          </div>

          <div className="space-y-4 text-xl md:text-2xl font-bold text-gray-900 text-center mb-8" data-id="element-951">
            <p data-id="element-952">¿Por qué tu hijo se enferma cada vez que empieza el jardín?</p>
            <p data-id="element-953">¿Por qué tiene mocos que no se van nunca?</p>
            <p data-id="element-954">
              ¿Por qué los antibióticos funcionan… y a las 3 semanas vuelve a
              estar igual?
            </p>
          </div>

          <div className="text-center text-lg text-gray-700" data-id="element-955">
            <p data-id="element-956">La respuesta no está en la farmacia.</p>
            <p className="text-2xl font-black text-[#2E7D32] mt-2" data-id="element-957">
              Está en su intestino.
            </p>
          </div>
        </motion.section>

        {/* SECCIÓN 3: EL PROBLEMA REAL */}
        <motion.section {...fadeIn} className="mb-16" data-id="element-958">
          <div className="space-y-6 text-lg text-gray-700" data-id="element-959">
            <p data-id="element-960">
              Hay algo que la mayoría de los pediatras no te dice — no porque
              sean malos, sino porque el sistema médico está diseñado para{' '}
              <strong data-id="element-961">apagar incendios</strong>, no para construir muros
              cortafuegos.
            </p>

            <div className="bg-[#E8F5E9] border-l-4 border-[#2E7D32] p-6 rounded-r-2xl my-8" data-id="element-962">
              <p className="font-bold text-[#2E7D32] text-sm uppercase tracking-wider mb-2" data-id="element-963">
                El dato que cambia todo:
              </p>
              <p className="text-xl font-bold text-gray-900 italic" data-id="element-964">
                "El 70% del sistema inmune de tu hijo vive en su intestino. No
                en sus pulmones. No en su garganta. En su intestino."
              </p>
            </div>

            <p data-id="element-965">
              Cada vez que tu hijo come azúcar industrial, ultraprocesados o
              toma un antibiótico sin recuperación activa posterior — su
              ejército de defensa se debilita.
            </p>
            <p data-id="element-966">Y el ciclo se repite:</p>

            <div className="bg-white p-6 rounded-2xl shadow-sm text-center font-bold text-gray-900 border border-red-100" data-id="element-967">
              🔴 Jardín → Virus → Antibiótico → Microbiota destruida → Más
              vulnerable → Jardín → Virus → Antibiótico…
            </div>

            <p className="text-xl font-bold text-center mt-8" data-id="element-968">
              ¿Te suena conocido?
            </p>
            <p className="text-center" data-id="element-969">
              Ese ciclo no se corta con más medicamentos.
              <br data-id="element-970" />
              Se corta con el protocolo correcto.
            </p>
          </div>
        </motion.section>

        {/* SECCIÓN 4: LA SOLUCIÓN */}
        <motion.section {...fadeIn} className="bg-gradient-to-b from-[#E8F5E9] to-white rounded-[2.5rem] p-8 md:p-12 shadow-lg border border-[#C8E6C9] mb-16 text-center" data-id="element-971">
          <Shield className="w-16 h-16 text-[#2E7D32] mx-auto mb-6" data-id="element-972" />
          <h2 className="text-4xl md:text-5xl font-black text-[#2E7D32] mb-4" data-id="element-973">
            INMUNIDAD TOTAL
          </h2>
          <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-8" data-id="element-974">
            El Protocolo Avanzado de Salud Intestinal para Blindar las Defensas
            de tu Peque — Sin Medicamentos
          </h3>

          <img src="https://cdn.magicpatterns.com/uploads/3rBBj1uJZndD8HwAwzBq5b/libroinmunidadtotal.png" alt="Inmunidad Total Book" className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl mb-10" data-id="element-975" />

          <p className="text-lg text-gray-700 mb-8" data-id="element-976">
            Este no es otro libro de recetas. Es el{' '}
            <strong data-id="element-977">protocolo de 90 días</strong> que convierte la cocina de tu
            casa en el sistema inmune más poderoso que tu hijo puede tener.
          </p>

          <div className="text-left bg-white p-6 md:p-8 rounded-2xl shadow-sm" data-id="element-978">
            <p className="font-bold text-gray-900 text-xl mb-6" data-id="element-979">
              Lo que vas a lograr:
            </p>
            <ul className="space-y-4" data-id="element-980">
              {['Cortar el ciclo de enfermedades repetidas del jardín', 'Saber exactamente qué hacer en las primeras 2 horas de cualquier síntoma', 'Eliminar el estreñimiento y los mocos crónicos desde la raíz', 'Construir una microbiota fuerte que dure toda la infancia', 'Depender menos de la farmacia y más de tu heladera', 'Llegar al consultorio con respuestas, no con preguntas'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-981">
                  <CheckCircle2 className="w-6 h-6 text-[#4CAF50] shrink-0" data-id="element-982" />
                  <span className="text-gray-700 font-medium" data-id="element-983">{item}</span>
                </li>)}
            </ul>
          </div>
        </motion.section>

        {/* BOTÓN CTA INTERMEDIO */}
        <motion.div {...fadeIn} className="text-center mb-16" data-id="element-984">
          <button onClick={() => {
          const el = document.getElementById('oferta-especial');
          if (el) {
            const yOffset = -100;
            const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({
              top: y,
              behavior: 'smooth'
            });
          }
        }} className="inline-block bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-black text-xl md:text-2xl px-10 py-5 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.03] active:scale-95 animate-pulse" data-id="element-985">
            Sí quiero inmunidad total para mi hijo
          </button>
        </motion.div>

        {/* SECCIÓN 5: LO QUE INCLUYE */}
        <motion.section {...fadeIn} className="mb-16" data-id="element-986">
          <h3 className="text-2xl md:text-3xl font-black text-center text-gray-900 mb-8" data-id="element-987">
            9 Capítulos de Protocolo Avanzado:
          </h3>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-10" data-id="element-988">
            <div className="divide-y divide-gray-100" data-id="element-989">
              {[{
              icon: '🔬',
              title: 'Cap. 1',
              desc: 'El intestino como comandante del sistema inmune'
            }, {
              icon: '⚡',
              title: 'Cap. 2',
              desc: 'Protocolo SOS: Las primeras 2 horas que todo lo cambian'
            }, {
              icon: '🏛️',
              title: 'Cap. 3',
              desc: 'Los 5 Pilares del Blindaje Intestinal'
            }, {
              icon: '🌿',
              title: 'Cap. 4',
              desc: 'La Farmacia Natural de tu Alacena'
            }, {
              icon: '🍳',
              title: 'Cap. 5',
              desc: '15 Recetas Médicas Comestibles'
            }, {
              icon: '🎒',
              title: 'Cap. 6',
              desc: 'Protocolo Jardín Maternal — Blindaje Externo'
            }, {
              icon: '💚',
              title: 'Cap. 7',
              desc: 'Salud Digestiva Profunda — Adiós Estreñimiento'
            }, {
              icon: '📅',
              title: 'Cap. 8',
              desc: 'El Calendario Inmunológico Anual'
            }, {
              icon: '💊',
              title: 'Cap. 9',
              desc: 'Suplementación Inteligente (cuándo la comida no alcanza)'
            }].map((cap, i) => <div key={i} className="flex items-center p-4 hover:bg-gray-50 transition-colors" data-id="element-990">
                  <span className="text-2xl w-10 text-center shrink-0" data-id="element-991">
                    {cap.icon}
                  </span>
                  <span className="font-bold text-gray-900 w-20 shrink-0" data-id="element-992">
                    {cap.title}
                  </span>
                  <span className="text-gray-700" data-id="element-993">{cap.desc}</span>
                </div>)}
            </div>
          </div>

          <div className="bg-[#FFF3E0] rounded-2xl p-8 border border-[#FFE0B2]" data-id="element-994">
            <h4 className="font-black text-xl text-[#E65100] mb-6 flex items-center gap-2" data-id="element-995">
              <Gift className="w-6 h-6" data-id="element-996" /> Además incluye:
            </h4>
            <ul className="space-y-4" data-id="element-997">
              {[{
              title: 'Protocolo SOS hora a hora',
              desc: 'Qué hacer exactamente desde el minuto 0 de un síntoma'
            }, {
              title: 'Lista de compras "Farmacia Natural"',
              desc: 'Lo que tenés que tener siempre en casa para las 3 AM'
            }, {
              title: 'El Calendario Inmunológico de las 4 Estaciones',
              desc: 'Qué hacer en otoño, invierno, primavera y verano'
            }, {
              title: '15 Recetas Médicas Comestibles',
              desc: 'Gomitas de caldo de huesos, Sopa Escudo Dorado, Shot "Poción de Dragón" y más'
            }, {
              title: 'Guía de Suplementación Pediátrica',
              desc: 'Qué pedirle exactamente al pediatra (dosis de Vitamina D, Zinc y Omega 3)'
            }].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-998">
                  <Gift className="w-5 h-5 text-[#E65100] shrink-0 mt-0.5" data-id="element-999" />
                  <p className="text-gray-800" data-id="element-1000">
                    <strong className="text-gray-900" data-id="element-1001">{item.title}</strong> —{' '}
                    {item.desc}
                  </p>
                </li>)}
            </ul>
          </div>
        </motion.section>

        {/* SECCIÓN 6: LA OFERTA IRREPETIBLE */}
        <motion.section id="oferta-especial" {...fadeIn} className="bg-white rounded-[2rem] shadow-xl border-4 border-[#E65100] overflow-hidden mb-12" data-id="element-1002">
          <div className="bg-[#E65100] text-white text-center py-6 px-4" data-id="element-1003">
            <h2 className="text-2xl md:text-3xl font-black flex items-center justify-center gap-2" data-id="element-1004">
              <Clock className="w-8 h-8 animate-pulse" data-id="element-1005" />
              ESTA OFERTA DESAPARECE CUANDO CERRÁS ESTA PÁGINA
            </h2>
          </div>

          <div className="p-8 md:p-12" data-id="element-1006">
            <p className="text-center text-lg text-gray-700 mb-8" data-id="element-1007">
              Este protocolo tiene un valor real de <strong data-id="element-1008">$99 USD</strong>. No
              lo vas a encontrar en redes. No va a estar disponible por separado
              en ningún otro momento.
              <br data-id="element-1009" />
              <br data-id="element-1010" />
              Esta es la única vez que podés sumarlo a tu pedido por un precio
              especial — porque acabás de comprar NutriPeques y queremos que
              tengas el sistema completo.
            </p>

            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-8" data-id="element-1011">
              <h3 className="text-center font-black text-gray-900 mb-4" data-id="element-1012">
                RESUMEN DE VALOR
              </h3>
              <div className="space-y-3 mb-6" data-id="element-1013">
                {[{
                name: 'Protocolo completo de 9 capítulos',
                val: '$47.00'
              }, {
                name: '15 Recetas Médicas Comestibles',
                val: '$18.00'
              }, {
                name: 'Protocolo SOS hora a hora',
                val: '$12.00'
              }, {
                name: 'Calendario Inmunológico Anual',
                val: '$10.00'
              }, {
                name: 'Guía de Suplementación Pediátrica',
                val: '$12.00'
              }].map((item, i) => <div key={i} className="flex justify-between items-center border-b border-gray-200 pb-2" data-id="element-1014">
                    <span className="text-gray-600" data-id="element-1015">{item.name}</span>
                    <span className="text-gray-400 line-through" data-id="element-1016">
                      {item.val}
                    </span>
                  </div>)}
                <div className="flex justify-between items-center pt-2" data-id="element-1017">
                  <span className="font-bold text-gray-900" data-id="element-1018">VALOR TOTAL</span>
                  <span className="font-bold text-gray-400 line-through" data-id="element-1019">
                    $99.00 USD
                  </span>
                </div>
                <div className="flex justify-between items-center bg-[#E8F5E9] p-3 rounded-xl mt-4" data-id="element-1020">
                  <span className="font-black text-[#2E7D32]" data-id="element-1021">
                    PRECIO ESPECIAL HOY
                  </span>
                  <span className="font-black text-[#2E7D32] text-xl" data-id="element-1022">
                    $27.00 USD
                  </span>
                </div>
              </div>
            </div>

            <div className="text-center mb-8" data-id="element-1023"></div>

            {/* CARTEL DE DESCUENTO PARPADEANTE */}
            <div className="relative mb-8" data-id="element-1024">
              {/* Badge de descuento */}
              <div className="bg-gradient-to-r from-[#E65100] via-[#FF6D00] to-[#E65100] rounded-2xl p-6 md:p-8 border-4 border-yellow-400 shadow-2xl relative overflow-hidden" data-id="element-1025">
                {/* Efecto de brillo animado */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_2s_infinite]" style={{
                animationName: 'shimmer',
                animationDuration: '2s',
                animationIterationCount: 'infinite',
                animationTimingFunction: 'linear'
              }} data-id="element-1026" />

                {/* Etiqueta de descuento parpadeante */}
                <div className="flex justify-center mb-4" data-id="element-1027">
                  <span className="inline-block bg-yellow-400 text-[#E65100] font-black text-2xl md:text-5xl px-4 md:px-6 py-2 md:py-3 rounded-xl shadow-lg animate-pulse transform rotate-[-2deg] whitespace-nowrap" data-id="element-1028">
                    🔥 73% OFF 🔥
                  </span>
                </div>

                <p className="text-white text-center text-lg md:text-xl font-bold mb-3" data-id="element-1029">
                  El precio real es{' '}
                  <span className="line-through text-yellow-200" data-id="element-1030">$99 USD</span>
                </p>
                <p className="text-yellow-300 text-center text-2xl md:text-3xl font-black mb-4 animate-pulse" data-id="element-1031">
                  ¡AHORA MISMO podés conseguirlo por solo $27 USD!
                </p>

                <div className="bg-red-900/40 rounded-xl p-4 border border-red-400/50 mb-4" data-id="element-1032">
                  <p className="text-white text-center font-bold text-base md:text-lg flex items-center justify-center gap-2" data-id="element-1033">
                    <span className="animate-ping inline-block w-3 h-3 bg-red-500 rounded-full" data-id="element-1034" />
                    ⚠️ Una vez que abandonés esta página, perdés esta
                    oportunidad PARA SIEMPRE
                    <span className="animate-ping inline-block w-3 h-3 bg-red-500 rounded-full" data-id="element-1035" />
                  </p>
                </div>

                <p className="text-yellow-200 text-center text-sm" data-id="element-1036">
                  Este descuento NO estará disponible en ningún otro lugar ni en
                  ningún otro momento.
                </p>
              </div>

              {/* Texto indicativo (NO es botón) */}
              <div className="mt-6 text-center" data-id="element-1037">
                <p className="text-xl md:text-2xl font-black text-[#2E7D32] leading-relaxed animate-bounce" data-id="element-1038">
                  👇 Conseguilo tocando el botón verde de abajo 👇
                </p>
              </div>
            </div>

            {/* HOTMART WIDGET CONTAINER */}
            <div id="hotmart-sales-funnel" className="min-h-[100px] flex items-center justify-center" data-id="element-1039">
              {/* Widget mounts here */}
            </div>
          </div>
        </motion.section>

        {/* SECCIÓN 7: URGENCIA REAL */}
        <motion.section {...fadeIn} className="bg-red-50 border border-red-200 rounded-2xl p-8 mb-12" data-id="element-1040">
          <h4 className="font-black text-red-700 flex items-center gap-2 mb-4" data-id="element-1041">
            <AlertCircle className="w-6 h-6" data-id="element-1042" /> ATENCIÓN:
          </h4>
          <div className="space-y-4 text-red-900" data-id="element-1043">
            <p data-id="element-1044">
              Esta oferta existe <strong data-id="element-1045">únicamente</strong> en esta página y{' '}
              <strong data-id="element-1046">únicamente</strong> en este momento.
            </p>
            <p data-id="element-1047">
              Si cerrás esta página o hacés click en "No gracias", el precio
              especial de $27 USD desaparece para siempre. No habrá forma de
              recuperarlo más adelante al mismo precio.
            </p>
            <p data-id="element-1048">
              El sistema no permite aplicar este descuento en ninguna otra
              instancia de compra.
            </p>
          </div>
        </motion.section>

        {/* SECCIÓN 8: CIERRE EMOCIONAL */}
        <motion.section {...fadeIn} className="text-center mb-16" data-id="element-1049">
          <div className="text-lg text-gray-700 space-y-4 mb-10" data-id="element-1050">
            <p data-id="element-1051">
              Dentro de 90 días podés ser la mamá que llega al jardín sabiendo
              que su hijo tiene el sistema inmune más fuerte de la sala.
            </p>
            <p data-id="element-1052">La que no entra en pánico cuando aparece el primer moco.</p>
            <p data-id="element-1053">
              La que sabe exactamente qué poner en el plato para que el cuerpo
              de su hijo se defienda solo.
            </p>
            <p className="font-bold text-gray-900 text-xl mt-8" data-id="element-1054">
              O podés cerrar esta página y seguir en el mismo ciclo de
              antibióticos, mocos y preocupación.
            </p>
            <p data-id="element-1055">La decisión es tuya. Y la tomás ahora.</p>
          </div>
        </motion.section>
      </main>

      <Footer data-id="element-1056" />
      <UpsellPurchaseToast data-id="element-1057" />
    </div>;
}